import { BitstreamReader, BitstreamWriter } from "@astronautlabs/bitstream";
import copy from "copy-to-clipboard";
import React from "react";
import { createRoot } from "react-dom/client";

import { ModalType, showModal } from "./modal";
import { FullSkillData, getRequiredPointsForRow, getUsedSkillPoints, Prof } from "./skill-simulator/SkillData";
import { MasterySkill } from "./skill-simulator/SkillMastery";
import { getGlobalSkillData, getUniqueMasterySkillID, loadGlobalSkillData, SkillLevels, SkillSimulator } from "./skill-simulator/SkillSimulator";

export function initSkillSimulator() {
    const parent = document.querySelector(".mw-skill-simulator");
    if (!parent) return;

    loadSimulator(parent as HTMLElement);
}

interface SkillSimulatorWrapperState {
    prof: Prof;
    lvl: number;
    visualLvl?: string;
    skillLevels: SkillLevels;
    masterySkills: MasterySkill[];
    masteryRepeat: boolean;
    version?: number;

    skillsJustCopied?: boolean;
    linkJustCopied?: boolean;
}

interface GargonemImportState {
    level: number;
    prof: string;
    skills: Record<number, number>[];
    mastery?: {
        skills: number[];
        repeat: boolean;
    };
}

interface SkillSimulatorWrapperProps {
    version: number;
}

const profIds: Record<Prof, number> = {
    [Prof.WARRIOR]: 0,
    [Prof.PALADIN]: 1,
    [Prof.DANCER]: 2,
    [Prof.MAGE]: 3,
    [Prof.HUNTER]: 4,
    [Prof.TRACKER]: 5,
};

const profIdsReverse: Record<number, Prof> = {
    [0]: Prof.WARRIOR,
    [1]: Prof.PALADIN,
    [2]: Prof.DANCER,
    [3]: Prof.MAGE,
    [4]: Prof.HUNTER,
    [5]: Prof.TRACKER,
};

enum BlockType {
    SkillLevels = 0,
    Mastery = 1,
    _Reserved = 2,
    Version = 3,
}

function skillLevelsByRows(prof: Prof, levels: SkillLevels) {
    const data = getGlobalSkillData()!;
    const tree = data.skillTrees[prof];
    const res: Record<number, number>[] = [];
    for (const row of tree) {
        let ind = res.length;
        res.push({});
        for (const id of row) {
            if (levels[id]) {
                res[ind][id] = levels[id];
            }
        }
    }
    return res;
}

class BufferSink implements UnderlyingSink<Uint8Array> {
    private buffer: Uint8Array;
    private offset = 0;
    private allocationSize: number;
    private result: Uint8Array | null = null;
    private resultAwaiters: Function[] = [];
    constructor(initialSize: number) {
        this.allocationSize = initialSize;
        this.buffer = new Uint8Array(initialSize);
    }
    start() {
        this.buffer = new Uint8Array(this.allocationSize);
        this.offset = 0;
    }
    write(chunk: Uint8Array) {
        if (this.buffer.byteLength < chunk.byteLength + this.offset) {
            const newBuffer = new Uint8Array(chunk.byteLength + this.offset + this.allocationSize);
            newBuffer.set(this.buffer);
            this.buffer = newBuffer;
        }
        this.buffer.set(chunk, this.offset);
        this.offset += chunk.byteLength;
    }
    abort() {
        this.close();
    }
    close() {
        this.result = this.buffer.slice(0, this.offset);
        for (const awaiter of this.resultAwaiters) {
            awaiter(this.result);
        }
    }
    async getResult(): Promise<Uint8Array> {
        if (this.result) {
            return this.result;
        } else {
            return new Promise((resolve) => {
                this.resultAwaiters.push(resolve);
            });
        }
    }
}

// The encoded state format is as follows:
// - 3 bits for profession ID
// - 9 bits for level
// - Array of:
//   - 2 bits indicating the Block Type
//   - The Block
//
// Block Types:
// 00: Skill Levels
// - Array of variable-length skill levels (in order specified by the skill tree)
//   - If 1st bit is 0, skill level is 0
//   - If 1st bit is 1, the next 4 bits indicate the level
// - 5 bit block terminator: 0b11111 (as if the skill level was 15)
// 01: Mastery
// - 1 bit for the repeat flag
// - list of short 6-bit skill IDs (indicating position in the skill tree rather than their actual ID)
// - terminated by a sentinel = 63
// 10: TBD
// 11: Version (no cap on bit count, increment size as needed)
const VersionBlockSize = 4;

enum PackedMasterySkillID {
    SENTINEL = 63,
    STEP = 62,
    STRIKE = 61,
}

function findMasterySkillShortID(prof: Prof, id: number) {
    if (id == -1) {
        return PackedMasterySkillID.STRIKE;
    }
    if (id == -2) {
        return PackedMasterySkillID.STEP;
    }
    const tree = getGlobalSkillData()!.skillTrees[prof];
    for (let row = 0; row < tree.length; ++row) {
        for (let i = 0; i < tree[row].length; ++i) {
            if (tree[row][i] == id) {
                return row * 6 + i;
            }
        }
    }
    return PackedMasterySkillID.STRIKE;
}

async function encodeSimulatorState(state: SkillSimulatorWrapperState, version: number): Promise<string> {
    const sink = new BufferSink(40);
    const stream = new WritableStream(sink);
    const writer = stream.getWriter();
    const bitWriter = new BitstreamWriter(writer);

    // Header
    bitWriter.write(3, profIds[state.prof]);
    bitWriter.write(9, state.lvl);

    // Write Skill Levels block
    bitWriter.write(2, BlockType.SkillLevels);

    const data = getGlobalSkillData()!;
    const ownTree = data.skillTrees[state.prof];
    const investedPts = getUsedSkillPoints(state.skillLevels);
    for (let row = 0; row < ownTree.length; ++row) {
        const requiredPts = getRequiredPointsForRow(row);
        if (requiredPts > investedPts) break;
        const set = ownTree[row];
        for (const id of set) {
            const lvl = state.skillLevels[id] ?? 0;
            if (lvl == 0) {
                bitWriter.write(1, 0);
            } else {
                bitWriter.write(1, 1);
                bitWriter.write(4, lvl);
            }
        }
    }

    // Write Skill Levels Block terminator
    bitWriter.write(1, 1);
    bitWriter.write(4, 15);

    if (state.masterySkills.length) {
        // Write Mastery Block
        bitWriter.write(2, BlockType.Mastery);
        bitWriter.write(1, state.masteryRepeat ? 1 : 0);
        for (const { skill } of state.masterySkills) {
            const id = findMasterySkillShortID(state.prof, skill);
            bitWriter.write(6, id);
        }
        bitWriter.write(6, PackedMasterySkillID.SENTINEL);
    }

    // Write Version Block
    bitWriter.write(2, BlockType.Version);
    bitWriter.write(VersionBlockSize, version);

    bitWriter.end();
    writer.close();

    const res = await sink.getResult();
    // @ts-expect-error This works typescript is stupid
    const blob = new Blob([res]);
    const reader = new FileReader();
    return new Promise((resolve) => {
        reader.addEventListener("load", () => {
            resolve((reader.result as string).split(",")[1]);
        });
        reader.readAsDataURL(blob);
    });
}

function importRawJsonState(stateString: string): SkillSimulatorWrapperState {
    const gargonemState: GargonemImportState = JSON.parse(decodeURIComponent(stateString));
    if (!gargonemState || typeof gargonemState != "object") throw new Error(`Invalid state`);
    if (typeof profIds[gargonemState.prof as Prof] == "undefined") throw new Error(`Invalid prof`);
    if (typeof gargonemState.level != "number") throw new Error(`Invalid lvl`);

    const mappedSkillLevels: Record<number, number> = {};
    if (!Array.isArray(gargonemState.skills)) throw new Error(`Invalid skills`);
    for (const entry of gargonemState.skills) {
        if (!entry || typeof entry != "object") throw new Error(`Invalid skills entry #1`);
        if (Object.keys(entry).some((k) => isNaN(Number(k))) || Object.values(entry).some((v) => isNaN(v))) throw new Error(`Invalid skills entry #2`);

        for (const key in entry) {
            mappedSkillLevels[Number(key)] = entry[key];
        }
    }

    if (!Array.isArray(gargonemState.mastery?.skills)) throw new Error(`Invalid mastery`);
    if (gargonemState.mastery.skills.some((v) => isNaN(Number(v)))) throw new Error(`Invalid mastery entry`);
    const mappedMasterySkills: MasterySkill[] = gargonemState.mastery.skills.map((s, idx) => ({ skill: s, id: idx }));

    const lvl = Math.max(Math.min(300, gargonemState.level), 1);
    return {
        prof: gargonemState.prof as Prof,
        lvl: lvl,
        visualLvl: String(lvl),
        skillLevels: mappedSkillLevels,
        masterySkills: mappedMasterySkills,
        masteryRepeat: !!gargonemState.mastery?.repeat,
    };
}

async function decodeSimulatorState(encoded: string) {
    if (encoded.startsWith("import:")) {
        return importRawJsonState(encoded.substring("import:".length));
    }
    const res = await fetch(`data:application/octet-stream;base64,${encoded}`);
    const buffer = await res.arrayBuffer();
    const reader = new BitstreamReader();
    reader.addBuffer(new Uint8Array(buffer));

    const profId = reader.readSync(3);
    const lvl = reader.readSync(9);
    const state: SkillSimulatorWrapperState = {
        prof: profIdsReverse[profId],
        lvl: lvl,
        skillLevels: {},
        masterySkills: [],
        masteryRepeat: true,
    };

    const data = getGlobalSkillData()!;
    const tree = data.skillTrees[state.prof];

    // Read blocks
    while (reader.available >= 2) {
        const blockType = reader.readSync(2);
        if (blockType == BlockType.SkillLevels) {
            let row = 0;
            let idx = 0;
            while (reader.available) {
                if (idx >= tree[row].length) {
                    idx = 0;
                    ++row;
                }
                const id = tree[row]?.[idx++];
                const hasLvl = reader.readSync(1);
                if (hasLvl) {
                    const lvl = reader.readSync(4);
                    if (lvl == 15) {
                        // Hit the terminator
                        break;
                    } else {
                        state.skillLevels[id] = lvl;
                    }
                }
            }
        } else if (blockType == BlockType.Mastery) {
            const masterySkills: MasterySkill[] = [];
            state.masteryRepeat = !!reader.readSync(1);
            while (reader.available) {
                const shortID = reader.readSync(6);
                if (shortID == PackedMasterySkillID.SENTINEL) break;

                let id = 0;
                if (shortID == PackedMasterySkillID.STEP) id = -2;
                else if (shortID == PackedMasterySkillID.STRIKE) id = -1;
                else {
                    const row = Math.floor(shortID / 6);
                    const idx = shortID % 6;
                    const tree = data.skillTrees[state.prof];
                    id = tree[row][idx] ?? -1;
                }
                masterySkills.push({
                    id: getUniqueMasterySkillID(),
                    skill: id,
                });
            }
            state.masterySkills = masterySkills;
        } else if (blockType == BlockType.Version) {
            state.version = reader.readSync(VersionBlockSize);
        } else {
            throw new Error(`Unknown Block type: ${blockType}`);
        }
    }

    return state;
}

class SkillSimulatorWrapper extends React.Component<SkillSimulatorWrapperProps, SkillSimulatorWrapperState> {
    private lastHash: string = "";
    private skillsCopyTimeout = -1;
    private linkCopyTimeout = -1;
    private enforceVisualLevelTimeout = -1;
    constructor(props: SkillSimulatorWrapperProps) {
        super(props);
        this.state = {
            prof: Prof.WARRIOR,
            lvl: 50,
            visualLvl: "50",
            skillLevels: {},
            masterySkills: [],
            masteryRepeat: true,
        };
        this.onSkillLevelChange = this.onSkillLevelChange.bind(this);
        this.onHashChange = this.onHashChange.bind(this);
        this.onLvlInputUpdate = this.onLvlInputUpdate.bind(this);
        this.onProfInputUpdate = this.onProfInputUpdate.bind(this);
        this.onSkillResetRequest = this.onSkillResetRequest.bind(this);
        this.onLinkCopyRequest = this.onLinkCopyRequest.bind(this);
        this.onSkillsCopyRequest = this.onSkillsCopyRequest.bind(this);
        this.onMasteryRepeatChange = this.onMasteryRepeatChange.bind(this);
        this.onMasterySkillsChange = this.onMasterySkillsChange.bind(this);
        this.onLvlInputBlur = this.onLvlInputBlur.bind(this);
        window.addEventListener("hashchange", this.onHashChange);
    }
    componentDidMount(): void {
        this.loadFromPageHash();
    }
    componentWillUnmount(): void {
        window.removeEventListener("hashchange", this.onHashChange);
    }
    componentDidUpdate(): void {
        this.updatePageHash();
    }
    private enforceVisualLevel() {
        const n = Number(this.state.visualLvl);
        if (!isNaN(n)) {
            this.setState({
                lvl: Math.min(Math.max(24, n), 500),
            });
        }
    }
    private onHashChange() {
        if (location.hash.substring(1) != this.lastHash) {
            this.loadFromPageHash();
        }
    }
    private async loadFromPageHash() {
        console.log(`loadFromPageHash`);
        const hash = location.hash.substring(1);
        if (hash.length == 0) return;
        try {
            const state = await decodeSimulatorState(hash);
            if (typeof state.version != "undefined" && state.version != this.props.version) {
                showModal({
                    title: "Stare umiejętności",
                    content: "Wczytany zestaw umiejętności został utworzony przed ostatnią aktualizacją - może nie działać poprawnie!",
                });
            }
            this.lastHash = hash;
            state.visualLvl = String(state.lvl);
            this.setState(state);
        } catch (e) {
            console.error(`SkillSimulatorWrapper: loadFromPageHash: parse error:`, e);
        }
    }
    private async updatePageHash() {
        const hash = await encodeSimulatorState(this.state, this.props.version);
        this.lastHash = hash;
        // Using replaceState in order to not fill the entire history with all the different hashes
        // @ts-expect-error TODO does this have to be like this? lol
        history.replaceState(undefined, undefined, `#${hash}`);
    }
    private onSkillLevelChange(skillLevels: SkillLevels) {
        this.setState({
            skillLevels: skillLevels,
        });
    }
    private onMasterySkillsChange(skills: MasterySkill[]) {
        this.setState({ masterySkills: skills });
    }
    private onMasteryRepeatChange(repeat: boolean) {
        this.setState({ masteryRepeat: repeat });
    }
    private onLvlInputUpdate(e: React.ChangeEvent<HTMLInputElement>) {
        const n = Number(e.target.value);
        if (!isNaN(n)) {
            if (this.enforceVisualLevelTimeout != -1) {
                clearTimeout(this.enforceVisualLevelTimeout);
            }
            this.enforceVisualLevelTimeout = setTimeout(() => {
                this.enforceVisualLevel();
                this.enforceVisualLevelTimeout = -1;
            }, 1500);
        }
        this.setState({
            visualLvl: e.target.value,
        });
    }
    private onLvlInputBlur() {
        if (this.enforceVisualLevelTimeout != -1) {
            clearTimeout(this.enforceVisualLevelTimeout);
        }
        this.enforceVisualLevel();
    }
    private onProfInputUpdate(e: React.ChangeEvent<HTMLSelectElement>) {
        this.setState({
            prof: e.target.value as Prof,
        });
    }
    private onSkillsCopyRequest() {
        if (this.skillsCopyTimeout != -1) {
            clearTimeout(this.skillsCopyTimeout);
        }
        this.skillsCopyTimeout = setTimeout(() => {
            this.setState({ skillsJustCopied: false });
            this.skillsCopyTimeout = -1;
        }, 3000);
        this.setState({ skillsJustCopied: true });
        copy(
            JSON.stringify({
                level: this.state.lvl,
                prof: this.state.prof,
                skills: skillLevelsByRows(this.state.prof, this.state.skillLevels),
                mastery: {
                    skills: this.state.masterySkills.map((s) => s.skill),
                    repeat: this.state.masteryRepeat,
                },
            }),
        );
    }
    private onLinkCopyRequest() {
        if (this.linkCopyTimeout != -1) {
            clearTimeout(this.linkCopyTimeout);
        }
        this.linkCopyTimeout = setTimeout(() => {
            this.setState({ linkJustCopied: false });
            this.linkCopyTimeout = -1;
        }, 3000);
        this.setState({ linkJustCopied: true });
        copy(location.href);
    }
    private onSkillResetRequest() {
        showModal({
            title: "Resetowanie umiejętności",
            content: "Czy na pewno chcesz zresetować wszystkie umiejętności?",
            type: ModalType.CONFIRM,
            confirmClb: () => {
                this.setState({ skillLevels: {}, masteryRepeat: true, masterySkills: [] });
            },
        });
    }
    render(): React.ReactNode {
        return [
            <div key={0} className="skill-simulator-controls">
                <div className="p1 input-group controls">
                    <label className="input-group-text" htmlFor="prof">
                        Profesja
                    </label>
                    <select value={this.state.prof} onChange={this.onProfInputUpdate} className="form-select" id="prof">
                        <option value={Prof.WARRIOR}>Wojownik</option>
                        <option value={Prof.PALADIN}>Paladyn</option>
                        <option value={Prof.DANCER}>Tancerz ostrzy</option>
                        <option value={Prof.MAGE}>Mag</option>
                        <option value={Prof.HUNTER}>Łowca</option>
                        <option value={Prof.TRACKER}>Tropiciel</option>
                    </select>
                    <label className="input-group-text" htmlFor="lvl">
                        Poziom
                    </label>
                    <input
                        onBlur={this.onLvlInputBlur}
                        value={this.state.visualLvl ?? this.state.lvl}
                        type="number"
                        onChange={this.onLvlInputUpdate}
                        className="form-control"
                        id="lvl"
                        onKeyDown={(e) => {
                            if (e.key == "Enter") this.onLvlInputBlur();
                        }}
                    />
                </div>
                <div className="p1 input-group buttons">
                    <button
                        onClick={this.onSkillsCopyRequest}
                        data-tip="Kopiuje umiejętności w formacie który można wkleić do dodatku z zestawu Gargonem."
                        className="btn btn-secondary"
                    >
                        {this.state.skillsJustCopied ? "Skopiowano!" : "Kopiuj do dodatku"}
                    </button>
                    <button onClick={this.onLinkCopyRequest} className="btn btn-primary">
                        {this.state.linkJustCopied ? "Skopiowano!" : "Kopiuj link"}
                    </button>
                    <button onClick={this.onSkillResetRequest} data-tip="Resetuj umiejętności" className="btn btn-danger">
                        ⨯
                    </button>
                </div>
            </div>,
            <SkillSimulator
                key={1}
                prof={this.state.prof}
                lvl={this.state.lvl}
                skillLevels={this.state.skillLevels}
                onChange={this.onSkillLevelChange}
                masterySkills={this.state.masterySkills}
                masteryRepeat={this.state.masteryRepeat}
                onMasterySkillsChange={this.onMasterySkillsChange}
                onMasteryRepeatChange={this.onMasteryRepeatChange}
            />,
        ];
    }
}

interface FullSkillDataEx extends FullSkillData {
    version: number;
}

async function loadSimulator(parent: HTMLElement) {
    const res = await fetch("/tools/skills/data");
    const json = (await res.json()) as FullSkillDataEx;
    loadGlobalSkillData(json);

    const root = createRoot(parent);
    root.render(<SkillSimulatorWrapper version={json.version} />);
}
